import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { Observable, Subscription, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { TopicService } from '../content/topics/topics.service';
import { CultureService } from '../cultures/culture.service';
import { reloadOnLocaleChange } from '../language';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../loaders';
import { PageTitleService } from '../navigation/title/title.service';

/** What a landing page is about: a national culture or a platform topic, the two things a reader subscribes to. */
export type LandingPageOf = 'culture' | 'topic';

/**
 * A culture's or a topic's landing page, `/cultures/{slug}` and
 * `/topics/{slug}`: where a subscription email about an insight sends the
 * reader, since whether they may see the insight itself is unknown.
 *
 * **A stub:** the name and a holding line. The full page — counts of insights,
 * episodes and news for the culture or topic — is yet to be built.
 *
 * The route says which kind in its data, as `landingPageOf`.
 */
@Component({
  selector: 'multisite-landing-page',
  templateUrl: './landing-page.component.html',
  imports: [TranslocoModule, AlertModule, LoaderGlobeAnimationDetailComponent],
})
export class LandingPageComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private cultureService = inject(CultureService);
  private topicService = inject(TopicService);
  private loadingService = inject(LoadingService);
  private pageTitleService = inject(PageTitleService);

  private readonly landingPageOf: LandingPageOf = this.route.snapshot.data['landingPageOf'];

  name = signal<string | null>(null);
  notFound = signal(false);
  failed = signal(false);

  loadingObject = signal<LoadingObject>({});
  isAnythingLoading = computed(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  private subscriptions: Subscription[] = [];
  private slug = '';

  constructor() {
    this.subscriptions.push(
      this.route.paramMap.subscribe((params) => {
        this.slug = params.get('slug') ?? '';
        this.load();
      }),
    );

    reloadOnLocaleChange(() => this.load());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private load(): void {
    this.notFound.set(false);
    this.failed.set(false);
    this.setLoadingStatus('landing-page', true);

    this.subscriptions.push(
      this.nameOf(this.slug)
        .pipe(finalize(() => this.setLoadingStatus('landing-page', false)))
        .subscribe({
          next: (name) => {
            this.name.set(name);
            this.notFound.set(name === null);
            if (name !== null) {
              this.pageTitleService.setTitle(name);
            }
          },
          error: (error) => {
            this.name.set(null);
            error?.status === 404 ? this.notFound.set(true) : this.failed.set(true);
          },
        }),
    );
  }

  /** The culture's or topic's name in the reader's language, or null when there is none by that slug. */
  private nameOf(slug: string): Observable<string | null> {
    switch (this.landingPageOf) {
      case 'culture':
        return this.cultureService
          .getCultures('geographic', 'national', false)
          .pipe(map((cultures) => cultures.find((culture) => culture.slug === slug)?.name ?? null));
      case 'topic':
        return this.topicService.getTopic(slug, false).pipe(map((topic) => topic?.name ?? null));
      default:
        return of(null);
    }
  }

  private setLoadingStatus(key: string, value: boolean): void {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  }
}
